import { useSelector } from 'react-redux';
import Button from '@components/Button';
import { Input } from '@components/Input';
import PhoneInput from '@components/PhoneInput';
import { Typography } from '@components/Typography';
import DragAndDrop, { SortableItem } from '@components/DragAndDrop';
import { BuilderCard, BuilderCardHeader } from '@components/BuilderCard';
import AddDropdownHeader from '@components/AddDropdownHeader';
import { useTranslation } from '@hooks';
import { LOCALE } from '@constants/locale';
import { WA_BUTTON_TYPE, WA_LIMITS, WA_LINK_BASES, WA_LINK_TYPE } from '@constants/campaignTemplates';
import { isAbsoluteHttpUrl } from './waTemplateUtils';
import { TwoColumnsRow } from './styles';

// Authoring kinds shown to the user; survey/coupon are URL buttons with a pinned base and
// dynamic suffix — the destination is never editable, the campaign fills it at send.
const BUTTON_KIND = {
    QUICK_REPLY: 'quick_reply',
    WEBSITE: 'website',
    SURVEY: 'survey',
    COUPON: 'coupon',
    PHONE: 'phone',
};

const KIND_LABEL_KEYS = {
    [BUTTON_KIND.QUICK_REPLY]: 'TemplateLibrary.waButtonQuickReply',
    [BUTTON_KIND.WEBSITE]: 'TemplateLibrary.waButtonWebsite',
    [BUTTON_KIND.SURVEY]: 'TemplateLibrary.waButtonSurvey',
    [BUTTON_KIND.COUPON]: 'TemplateLibrary.waButtonCoupon',
    [BUTTON_KIND.PHONE]: 'TemplateLibrary.waButtonPhone',
};

const PHONE_COUNTRY_BY_LOCALE = {
    [LOCALE.UNITED_KINGDOM]: 'gb',
    [LOCALE.UKRAINE]: 'ua',
    [LOCALE.SPAIN]: 'es',
    [LOCALE.PORTUGAL]: 'pt',
    [LOCALE.BRAZIL]: 'br',
    [LOCALE.POLAND]: 'pl',
    [LOCALE.GERMANY]: 'de',
    [LOCALE.MEXICO]: 'mx',
};

const buttonKind = button => {
    if (button.type === WA_BUTTON_TYPE.QUICK_REPLY) return BUTTON_KIND.QUICK_REPLY;
    if (button.type === WA_BUTTON_TYPE.PHONE_NUMBER) return BUTTON_KIND.PHONE;
    if (button.type === WA_BUTTON_TYPE.URL && button.dynamic) {
        return button.linkType === WA_LINK_TYPE.COUPON ? BUTTON_KIND.COUPON : BUTTON_KIND.SURVEY;
    }
    return BUTTON_KIND.WEBSITE;
};

const createButton = kind => {
    const base = { text: '', url: '', dynamic: false, linkType: '', phoneNumber: '', isOpen: true };
    switch (kind) {
        case BUTTON_KIND.QUICK_REPLY:
            return { ...base, type: WA_BUTTON_TYPE.QUICK_REPLY };
        case BUTTON_KIND.PHONE:
            return { ...base, type: WA_BUTTON_TYPE.PHONE_NUMBER };
        case BUTTON_KIND.SURVEY:
            return {
                ...base,
                type: WA_BUTTON_TYPE.URL,
                dynamic: true,
                linkType: WA_LINK_TYPE.SURVEY,
                url: WA_LINK_BASES.SURVEY,
            };
        case BUTTON_KIND.COUPON:
            return {
                ...base,
                type: WA_BUTTON_TYPE.URL,
                dynamic: true,
                linkType: WA_LINK_TYPE.COUPON,
                url: WA_LINK_BASES.COUPON,
            };
        default:
            return { ...base, type: WA_BUTTON_TYPE.URL };
    }
};

const ButtonItem = ({ listeners, attributes, button, index, disabled, phoneCountry, onToggle, onPatch, onDelete }) => {
    const { t } = useTranslation();
    const kind = buttonKind(button);
    const urlInvalid = !!button.url.trim() && !isAbsoluteHttpUrl(button.url.trim());

    const labelInput = (
        <Input
            label={t('TemplateLibrary.buttonLabel')}
            fullWidth
            disabled={disabled}
            error={button.text.length > WA_LIMITS.BUTTON_LABEL}
            helperText={
                button.text.length > WA_LIMITS.BUTTON_LABEL
                    ? t('validation.maxLength', { count: WA_LIMITS.BUTTON_LABEL })
                    : null
            }
            inputProps={{
                value: button.text,
                onChange: e => onPatch(index, { text: e.target.value }),
            }}
        />
    );

    return (
        <BuilderCard isExpanded={button.isOpen}>
            <BuilderCardHeader
                caption={t(KIND_LABEL_KEYS[kind])}
                title={button.text || button.phoneNumber}
                isExpanded={button.isOpen}
                onToggle={() => onToggle(index)}
                listeners={listeners}
                attributes={attributes}
            />
            {button.isOpen ? (
                <>
                    {kind === BUTTON_KIND.WEBSITE && (
                        <TwoColumnsRow>
                            {labelInput}
                            <Input
                                label={t('common.link')}
                                fullWidth
                                disabled={disabled}
                                error={urlInvalid}
                                helperText={urlInvalid ? t('validation.invalidURL') : null}
                                inputProps={{
                                    value: button.url,
                                    onChange: e => onPatch(index, { url: e.target.value }),
                                }}
                            />
                        </TwoColumnsRow>
                    )}
                    {kind === BUTTON_KIND.PHONE && (
                        <TwoColumnsRow>
                            {labelInput}
                            <PhoneInput
                                label={t('common.phoneNumber')}
                                disabled={disabled}
                                autoFormat
                                country={phoneCountry}
                                value={button.phoneNumber.replace(/^\+/, '')}
                                onChange={value => onPatch(index, { phoneNumber: value ? `+${value}` : '' })}
                            />
                        </TwoColumnsRow>
                    )}
                    {kind !== BUTTON_KIND.WEBSITE && kind !== BUTTON_KIND.PHONE && labelInput}
                    {(kind === BUTTON_KIND.SURVEY || kind === BUTTON_KIND.COUPON) && (
                        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', marginBottom: '12px' }}>
                            {t(
                                kind === BUTTON_KIND.COUPON
                                    ? 'TemplateLibrary.couponLinkHint'
                                    : 'TemplateLibrary.surveyLinkHint',
                            )}
                        </Typography>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                        <Button variant="danger" onClick={() => onDelete(index)} disabled={disabled}>
                            {t('buttons.delete')}
                        </Button>
                    </div>
                </>
            ) : null}
        </BuilderCard>
    );
};

const ButtonsEditor = ({ buttons, onChange, disabled }) => {
    const { t } = useTranslation();
    const account = useSelector(state => state.account?.account);
    const phoneCountry = PHONE_COUNTRY_BY_LOCALE[account?.locale] || 'us';

    const urlCount = buttons.filter(button => button.type === WA_BUTTON_TYPE.URL).length;
    const phoneCount = buttons.filter(button => button.type === WA_BUTTON_TYPE.PHONE_NUMBER).length;
    const dynamicTaken = buttons.some(button => button.type === WA_BUTTON_TYPE.URL && button.dynamic);
    const atCapacity = buttons.length >= WA_LIMITS.BUTTONS;

    const addOptions = [
        { value: BUTTON_KIND.QUICK_REPLY, label: KIND_LABEL_KEYS[BUTTON_KIND.QUICK_REPLY], disabled: atCapacity },
        {
            value: BUTTON_KIND.WEBSITE,
            label: KIND_LABEL_KEYS[BUTTON_KIND.WEBSITE],
            disabled: atCapacity || urlCount >= WA_LIMITS.URL_BUTTONS,
        },
        {
            value: BUTTON_KIND.SURVEY,
            label: KIND_LABEL_KEYS[BUTTON_KIND.SURVEY],
            disabled: atCapacity || urlCount >= WA_LIMITS.URL_BUTTONS || dynamicTaken,
        },
        {
            value: BUTTON_KIND.COUPON,
            label: KIND_LABEL_KEYS[BUTTON_KIND.COUPON],
            disabled: atCapacity || urlCount >= WA_LIMITS.URL_BUTTONS || dynamicTaken,
        },
        {
            value: BUTTON_KIND.PHONE,
            label: KIND_LABEL_KEYS[BUTTON_KIND.PHONE],
            disabled: atCapacity || phoneCount >= 1,
        },
    ];

    const addButton = kind => onChange([...buttons, createButton(kind)]);

    const toggleButton = index =>
        onChange(buttons.map((button, i) => (i === index ? { ...button, isOpen: !button.isOpen } : button)));

    const patchButton = (index, patch) =>
        onChange(buttons.map((button, i) => (i === index ? { ...button, ...patch } : button)));

    const removeButton = index => onChange(buttons.filter((_, i) => i !== index));

    return (
        <div>
            <DragAndDrop items={buttons.map((_, index) => String(index))} data={buttons} onDragEndCb={onChange}>
                {buttons.map((button, index) => (
                    <SortableItem key={index} id={String(index)}>
                        <ButtonItem
                            button={button}
                            index={index}
                            disabled={disabled}
                            phoneCountry={phoneCountry}
                            onToggle={toggleButton}
                            onPatch={patchButton}
                            onDelete={removeButton}
                        />
                    </SortableItem>
                ))}
            </DragAndDrop>
            {!disabled && (
                <AddDropdownHeader
                    options={addOptions}
                    onAdd={addButton}
                    buttonText={t('TemplateLibrary.waAddButton')}
                    buttonProps={{ size: 'small' }}
                />
            )}
        </div>
    );
};

export default ButtonsEditor;
